import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiGithub, FiLinkedin, FiMail, FiDownload, FiArrowRight } from 'react-icons/fi';

const blink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(56, 189, 248, 0.2); }
  50% { box-shadow: 0 0 40px rgba(56, 189, 248, 0.4); }
`;

const Section = styled.section`
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
`;

const Content = styled(motion.div)`
  z-index: 10;
`;

const Label = styled(motion.div)`
  font-family: 'JetBrains Mono', monospace;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &::before {
    content: '';
    width: 24px;
    height: 2px;
    background: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }

  .keyword { color: #FF7B72; }
  .string { color: #A5D6FF; }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  line-height: 1.1;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.5rem;
  letter-spacing: -0.04em;
  
  .highlight {
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0.1em;
      left: 0;
      width: 100%;
      height: 0.3em;
      background: linear-gradient(90deg, ${props => props.theme.colors.primary}40, ${props => props.theme.colors.accent}40);
      z-index: -1;
      border-radius: 2px;
    }
  }
  
  span {
    color: ${props => props.theme.colors.textDim};
  }
`;

const Subtitle = styled(motion.div)`
  font-size: 1.35rem;
  color: ${props => props.theme.colors.textDim};
  font-weight: 300;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  
  strong {
    color: ${props => props.theme.colors.text};
    font-weight: 600;
  }
`;

const CodeBlock = styled(motion.div)`
  background: #0D1117;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  animation: ${float} 6s ease-in-out infinite;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const CodeHeader = styled.div`
  background: #161B22;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    
    &.red { background: #FF5F56; }
    &.yellow { background: #FFBD2E; }
    &.green { background: #27CA40; }
  }
  
  .filename {
    margin-left: 1rem;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.8rem;
    color: ${props => props.theme.colors.textDim};
  }
`;

const CodeContent = styled.div`
  padding: 1.5rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.8;
  counter-reset: line;
  min-height: 280px;
`;

const CodeLine = styled(motion.div)`
  display: flex;
  counter-increment: line;
  
  &::before {
    content: counter(line);
    min-width: 2rem;
    margin-right: 1.5rem;
    color: #484F58;
    text-align: right;
    user-select: none;
  }

  .keyword { color: #FF7B72; }
  .class-name { color: #FFA657; }
  .function { color: #D2A8FF; }
  .string { color: #A5D6FF; }
  .property { color: #79C0FF; }
  .comment { color: #8B949E; font-style: italic; }
  .operator { color: #FF7B72; }
  .this, .self { color: #FF7B72; }
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 1.2em;
  background: ${props => props.theme.colors.primary};
  margin-left: 2px;
  animation: ${blink} 1s infinite;
  vertical-align: text-bottom;
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: ${props => props.theme.colors.primary};
  color: #000;
  padding: 1rem 2rem;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(56, 189, 248, 0.4);
    
    &::before {
      left: 100%;
    }
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  padding: 1rem 2rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.3s;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    background: rgba(56, 189, 248, 0.05);
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  margin-top: 3rem;
  
  a {
    color: ${props => props.theme.colors.textDim};
    font-size: 1.5rem;
    transition: all 0.3s;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
      transform: translateY(-3px);
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

// 코드 라인 데이터
const codeLines = [
  { content: <><span className="keyword">class</span> <span className="class-name">Developer</span>:</> },
  { content: <>&nbsp;&nbsp;<span className="keyword">def</span> <span className="function">__init__</span>(<span className="self">self</span>):</> },
  { content: <>&nbsp;&nbsp;&nbsp;&nbsp;<span className="self">self</span>.name = <span className="string">"Ha Yul"</span></> },
  { content: <>&nbsp;&nbsp;&nbsp;&nbsp;<span className="self">self</span>.role = <span className="string">"Automation Engineer"</span></> },
  { content: <>&nbsp;&nbsp;&nbsp;&nbsp;<span className="self">self</span>.skills = [<span className="string">"Python"</span>, <span className="string">"React"</span>]</> },
  { content: <></> },
  { content: <>&nbsp;&nbsp;<span className="keyword">async</span> <span className="keyword">def</span> <span className="function">solve</span>(<span className="self">self</span>, problem):</> },
  { content: <>&nbsp;&nbsp;&nbsp;&nbsp;<span className="comment"># Analyzing bottlenecks...</span></> },
  { content: <>&nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">return</span> <span className="function">automate</span>(problem)</> },
];

const Hero = () => {
  const [visibleLines, setVisibleLines] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (visibleLines < codeLines.length) {
      const timer = setTimeout(() => {
        setVisibleLines(prev => prev + 1);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      // 모든 라인이 다 나온 후 커서 깜빡임 유지
      setShowCursor(true);
    }
  }, [visibleLines]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
    }
  };

  return (
    <Section id="hero">
      <Container>
        <Content
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Label variants={itemVariants}>
            <span className="keyword">status</span> = <span className="string">"Available for Work"</span>
          </Label>
          
          <Title variants={itemVariants}>
            <span className="highlight">Creating Value</span><br />
            <span>through</span> Automation.
          </Title>
          
          <Subtitle variants={itemVariants}>
            I'm <strong>Ha Yul</strong>, an engineer who transforms<br />
            repetitive tasks into elegant solutions.
          </Subtitle>

          <ButtonGroup variants={itemVariants}>
            <PrimaryButton 
              href="#projects"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              View Projects <FiArrowRight />
            </PrimaryButton>
            <SecondaryButton 
              href="/01_하율_이력서.pdf" 
              download="01_하율_이력서.pdf"
              target="_blank"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Download Resume <FiDownload />
            </SecondaryButton>
          </ButtonGroup>

          <SocialLinks variants={itemVariants}>
            <motion.a 
              href="https://github.com/CatYulHa" 
              target="_blank"
              whileHover={{ y: -3 }}
            >
              <FiGithub />
            </motion.a>
            <motion.a 
              href="https://linkedin.com/in/yul-ha-a6199929a" 
              target="_blank"
              whileHover={{ y: -3 }}
            >
              <FiLinkedin />
            </motion.a>
            <motion.a 
              href="https://mail.google.com/mail/?view=cm&fs=1&to=checkdata.yul@gmail.com"
              target="_blank"
              whileHover={{ y: -3 }}
            >
              <FiMail />
            </motion.a>
          </SocialLinks>
        </Content>

        <CodeBlock
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <CodeHeader>
            <div className="dot red" />
            <div className="dot yellow" />
            <div className="dot green" />
            <span className="filename">developer.py</span>
          </CodeHeader>
          <CodeContent>
            {codeLines.map((line, index) => (
              <CodeLine
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: index < visibleLines ? 1 : 0 }}
                transition={{ duration: 0.1 }}
              >
                {line.content}
                {index === visibleLines - 1 && showCursor && <Cursor />}
              </CodeLine>
            ))}
          </CodeContent>
        </CodeBlock>
      </Container>
    </Section>
  );
};

export default Hero;
